import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { ScheduleCatalogService } from "../../../services/v2/scheduleCatalog.service";
import { Articulo, ProspectCatalogs, ProspectDetails, Quote } from "../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { cuando, dinero, errorTexto, fechaDia, GESTION_RESULTADOS, nombreCompleto, plano, SIMBOLO_PLANTEL } from "./salesHelpers";

interface Props {
	campusId: number;
	det: ProspectDetails;
	onEditar: () => void;
	onEnviarAlChat: (texto: string) => void;
	onPaso: (paso: "promesa" | "llamar" | "no_interesa") => void;
	onAbrir: (id: number) => void;
	onQuitarVinculo: (linkId: number) => void;
	onPromo: () => void;
	onAgendar: (motivo: string) => void;
	catalogs: ProspectCatalogs | null;
	/** Corre una acción de la ficha (guarda, recarga la ficha y la conversación). */
	onAccion: (fn: () => Promise<any>) => Promise<void>;
}

const catalogo = new ScheduleCatalogService();
const cache: { [campus: number]: Promise<any[]> } = {};
const horariosDe = (campusId: number): Promise<any[]> => {
	if (!cache[campusId]) {
		cache[campusId] = catalogo.catalogue(campusId).then((r) => r.data?.horarios || []).catch(() => { delete cache[campusId]; return []; });
	}
	return cache[campusId];
};

/** Palabras del curso que dijo ("Pasteleria / Reposteria") para empatar con el catálogo de horarios. */
const palabras = (s: string | null | undefined): string[] =>
	plano(s).split(/[^a-zñ]+/).filter((w) => w.length >= 4 && !["curso", "cursos", "para", "info", "informacion", "clases", "diplomado"].includes(w));

const cortoCurso = (s: string) => s.replace(/^Diplomado de /i, "");

/**
 * Columna derecha del lienzo. Cambia con la Etapa: Pre-Inscripción (qué le interesa, horarios, cotización),
 * Matriculado (su matrícula, kit, qué ofrecerle hoy), Retirado (cuando estudió, qué ofrecerle hoy). Todo lo de
 * alumno viene de WAPP (det.wapp).
 */
const LadoCliente: React.FC<Props> = ({ campusId, det, onEditar, onEnviarAlChat, onPaso, onAbrir, onQuitarVinculo, onPromo, onAgendar, catalogs, onAccion }) => {
	const http = HttpManager.getInstance();
	const p = det.prospect;
	const w = det.wapp;
	const plantel = p.campus_id || campusId;
	const sim = SIMBOLO_PLANTEL[plantel] || "";
	const [horarios, setHorarios] = useState<any[] | null>(null);
	const [marcados, setMarcados] = useState<number[]>([]);
	const [quote, setQuote] = useState<Quote | null | undefined>(undefined);
	// Gestiones: abrir (objetivo + dueña) y cerrar (cómo terminó).
	const [nuevaG, setNuevaG] = useState<boolean>(false);
	const [gObjetivo, setGObjetivo] = useState<string>("");
	const [gDuena, setGDuena] = useState<string>("");
	const [gMotivo, setGMotivo] = useState<string>("");
	const [cerrarG, setCerrarG] = useState<number | null>(null);
	const [gResultado, setGResultado] = useState<string>("logrado");
	const [gNota, setGNota] = useState<string>("");
	// Vitrina: productos de la tienda del plantel para ofrecer por el chat.
	const [vitrina, setVitrina] = useState<Articulo[] | null>(null);
	const [vBusca, setVBusca] = useState<string>("");
	const [vError, setVError] = useState<string | null>(null);
	const buscarVitrina = (q: string) => {
		setVError(null);
		http.prospectsService.vitrina(campusId, q).then((r) => setVitrina(r.data)).catch((e) => { setVitrina([]); setVError(errorTexto(e)); });
	};
	useEffect(() => { setNuevaG(false); setCerrarG(null); setVitrina(null); setVBusca(""); }, [p.id]);
	const objetivosEtapa = (catalogs?.objetivos || []).filter((o) => String(o.etapas).split(",").map(Number).includes(p.etapa_id) && o.code !== "sin_pendientes");
	const abrirGestion = () => gObjetivo && onAccion(() => http.prospectsService.openGestion(campusId, p.id, { objetivo: gObjetivo, ownerZohoId: gDuena || null, reason: gMotivo || null }))
		.then(() => { setNuevaG(false); setGObjetivo(""); setGDuena(""); setGMotivo(""); });
	const cerrarGestion = (id: number) => onAccion(() => http.prospectsService.closeGestion(campusId, p.id, id, { outcome: gResultado, notes: gNota || null }))
		.then(() => { setCerrarG(null); setGNota(""); setGResultado("logrado"); });

	useEffect(() => {
		setMarcados([]); setHorarios(null);
		horariosDe(campusId).then(setHorarios);
	}, [campusId, p.id]);

	// Cotización (Pre-Inscripción y Retirado): precios reales de WAPP del curso que le interesa, o del que dejó.
	const cursoCotizar = p.etapa_id === 3 && w?.retiro ? w.retiro.curso : p.course_interest_text;
	useEffect(() => {
		setQuote(undefined);
		if (p.etapa_id === 2 || !cursoCotizar) { setQuote(null); return; }
		let vivo = true;
		http.prospectsService.quote(plantel, cursoCotizar).then((r) => { if (vivo) setQuote(r.data.quote); }).catch(() => { if (vivo) setQuote(null); });
		return () => { vivo = false; };
	}, [plantel, cursoCotizar, p.etapa_id]);

	const abiertos = useMemo(() => {
		if (!horarios) return [];
		const ws = palabras(cursoCotizar);
		if (!ws.length) return [];
		return horarios.filter((h) => h.plantel === plantel && h.estado === "por-comenzar"
			&& ws.some((wd) => plano(h.curso + " " + h.modulo).includes(wd.slice(0, 6))))
			.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio))).slice(0, 8);
	}, [horarios, cursoCotizar, plantel]);

	const enviarHorarios = () => {
		const txt = abiertos.filter((h) => marcados.includes(h.id)).map((h) =>
			`*Curso:* ${h.curso}${h.modulo && h.modulo !== h.curso ? " · " + h.modulo : ""}\n*Periodo:* ${h.periodo}\n*Horario:* ${h.horario}\nInicia: ${h.inicioTexto}`).join("\n\n");
		if (txt) onEnviarAlChat(txt);
	};
	const enviarCotizacion = () => {
		if (!quote) return;
		const q = quote;
		const lineas = [`*${cortoCurso(q.curso)}* · ${q.meses} meses`,
			`Inscripción: ${q.inscripcion > 0 ? dinero(q.inscripcion, q.simbolo) + " → " + q.inscripcion_gratis_texto : q.inscripcion_gratis_texto}`,
			`Mensualidad: ${dinero(q.mensualidad, q.simbolo)}`];
		if (q.kit) lineas.push(`${q.kit.nombre}: ${dinero(q.kit.precio, q.simbolo)}`);
		if (q.promo?.best) lineas.push(`Promo: ${q.promo.best.name} (${q.promo.best.why})`);
		lineas.push(`Hoy paga: ${dinero(q.hoy_paga, q.simbolo)} (${q.hoy_paga_detalle})`);
		onEnviarAlChat(lineas.join("\n"));
	};
	const accionOferta = (o: { kind: string; title: string; detail: string }) => {
		if (o.kind === "promo") return onPromo();
		if (o.kind === "kit") return onEnviarAlChat(`Hola ${(p.first_name || "").split(" ")[0]}! Le recuerdo que su kit quedó con saldo de ${o.detail.replace(" pendientes", "")}. Lo puede abonar en caja o por transferencia 😊`);
		if (o.kind === "retencion") return onAgendar("Retención: " + o.title);
		if (o.kind === "reingreso" && o.title.startsWith("Retoma")) return onEnviarAlChat(`Te puedo reactivar justo donde te quedaste, en ${o.detail.split(" · ")[0]}: no repites nada de lo que ya llevabas. Y la inscripción va gratis si vuelves esta semana.`);
		if (o.kind === "mas_cursos") return document.getElementById("lado-horarios")?.scrollIntoView({ behavior: "smooth" });
		return onAgendar(o.title);
	};

	const act = w?.active || null;
	const kits = w?.kits || [];
	const kitPendiente = kits.find((k) => k.estado === "Abono sin liquidar" || k.estado === "Cargado sin pago");

	return (
		<div className="cv-lado">
			{/* Pre-Inscripción / sin alumno: qué le interesa */}
			{!act && !w?.retiro ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Qué le interesa</h4><button className="enlace" onClick={onEditar}>Editar</button></div>
					<div className="par"><span>Curso</span><span>{p.course_interest_text || "—"}</span></div>
					<div className="par"><span>Plantel</span><span>{det.campuses.map((c) => c.name.replace("Washington Academy ", "")).join(", ") || "—"}</span></div>
					<div className="par"><span>Primer contacto</span><span>{cuando(p.first_contact_at)}</span></div>
					{det.source ? <div className="par"><span>Llegó por</span><span>{det.source}{p.ad_name ? " · «" + p.ad_name + "»" : ""}</span></div> : null}
					{p.phone_alt ? <div className="par"><span>Otro teléfono</span><span>{telefonoBonito(p.phone_alt)}</span></div> : null}
					{p.email ? <div className="par"><span>Correo</span><span>{p.email}</span></div> : null}
				</div>
			) : null}

			{/* Matriculado: su matrícula, kit, qué ofrecerle hoy */}
			{act ? (
				<>
					<div className="tarjeta">
						<div className="tarjeta-cab"><h4>Su matrícula</h4><button className="enlace" onClick={onEditar}>Editar datos</button></div>
						<div className="lado-linea"><b>{cortoCurso(act.curso)} · {act.modulo}</b><span className="apagado">{act.horario}{act.maestro ? " · " + act.maestro : ""}</span></div>
						<div className="par"><span>Mensualidades</span><span>{act.pagadas || 0} de {act.meses}</span></div>
						{act.proxima_fecha ? <div className="par"><span>Próxima</span><span className={act.vencidas ? "oro" : ""}>{fechaDia(act.proxima_fecha)}{act.proxima_monto ? " · " + dinero(act.proxima_monto, sim) : ""}{act.vencidas ? " · vencida" : ""}</span></div> : null}
						{act.asistencia ? <div className="par"><span>Asistencia</span><span className={act.asistencia.faltas_seguidas >= 3 ? "oro" : ""}>{act.asistencia.pct}%{act.asistencia.faltas_seguidas >= 3 ? " · " + act.asistencia.faltas_seguidas + " faltas seguidas" : ""}</span></div> : null}
						{act.end_date ? <div className="par"><span>Termina</span><span>{fechaDia(act.end_date)}{act.termina_en_dias !== null && act.termina_en_dias >= 0 && act.termina_en_dias <= 45 ? " · en " + act.termina_en_dias + " días" : ""}</span></div> : null}
						<div className="par"><span>Alumno desde</span><span>{cuando(act.created_at)}</span></div>
						{w!.enrollments.length > 1 ? <span className="apagado">{w!.enrollments.length - 1} matrícula(s) anteriores</span> : null}
					</div>
					<div className="tarjeta">
						<div className="tarjeta-cab"><h4>Kit</h4>{kitPendiente ? <span className="chip oro">{kitPendiente.estado}</span> : kits.length ? <span className="chip verde">{kits[0].estado}</span> : <span className="chip">Sin kit</span>}</div>
						{kits.slice(0, 3).map((k) => (
							<React.Fragment key={k.id}>
								<div className="par"><span>{k.kit}</span><span>{k.estado}</span></div>
								<div className="par"><span>Pagado / Total</span><span>{dinero(k.paid_amount, sim)} / {dinero(k.sub_total, sim)}</span></div>
								{k.pendiente > 0 && k.status_id !== 4 ? <div className="par"><span>Saldo por cobrar</span><span className="oro">{dinero(k.pendiente, sim)}</span></div> : null}
							</React.Fragment>
						))}
						{!kits.length ? <span className="apagado">No tiene kit cargado en WAPP.</span> : null}
					</div>
				</>
			) : null}

			{/* Retirado: cuando estudió */}
			{!act && w?.retiro ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Cuando estudió</h4><button className="enlace" onClick={onEditar}>Editar datos</button></div>
					<div className="lado-linea"><b>{cortoCurso(w.retiro.curso)} · {w.retiro.modulo}</b></div>
					<div className="par"><span>Avanzó</span><span>{w.retiro.avanzo} de {w.retiro.meses} meses</span></div>
					<div className="par"><span>Se retiró</span><span>{cuando(w.retiro.fecha)}</span></div>
					<div className="par"><span>Motivo</span><span>{w.retiro.motivo || w.retiro.ultima_nota || "sin motivo registrado"}</span></div>
					<div className="par"><span>Saldo al retirarse</span><span className={w.retiro.saldo > 0 ? "oro" : ""}>{dinero(w.retiro.saldo, sim)}</span></div>
					{w.retiro.asesora ? <div className="par"><span>Asesora de entonces</span><span>{w.retiro.asesora}</span></div> : null}
				</div>
			) : null}

			{/* Gestiones (lienzo Matriculado): cada objetivo con su propia dueña, además de la asesora de cabecera. */}
			{p.etapa_id !== 1 || det.gestiones.length ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Gestiones</h4>{!nuevaG && objetivosEtapa.length ? <button className="enlace" onClick={() => setNuevaG(true)}>+ Nueva</button> : null}</div>
					{det.gestiones.map((g) => (
						<div key={g.id} className="lado-gestion">
							<div className="par"><span><b>{g.objetivo}</b>{g.notes ? <><br /><span className="apagado">{g.notes}</span></> : null}</span>
								<span>{g.owner_name || <span className="apagado">sin dueña</span>}<br /><span className="apagado">{g.opened_by === "matricula" ? "al matricular" : "desde"} {cuando(g.opened_at)}</span></span></div>
							{cerrarG === g.id ? (
								<div className="lado-form">
									<select value={gResultado} onChange={(e) => setGResultado(e.target.value)} aria-label="Cómo terminó">
										{GESTION_RESULTADOS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
									</select>
									<input type="text" placeholder="Nota (opcional)" value={gNota} onChange={(e) => setGNota(e.target.value)} aria-label="Nota" />
									<div className="fila-botones">
										<button className="btn-wa pri" onClick={() => cerrarGestion(g.id)}>Cerrar gestión</button>
										<button className="btn-wa" onClick={() => setCerrarG(null)}>Cancelar</button>
									</div>
								</div>
							) : <button className="btn-wa mini" onClick={() => { setCerrarG(g.id); setGResultado("logrado"); setGNota(""); }}>Cerrar</button>}
						</div>
					))}
					{!det.gestiones.length && !nuevaG ? <span className="apagado">Sin gestiones abiertas.</span> : null}
					{nuevaG ? (
						<div className="lado-form">
							<select value={gObjetivo} onChange={(e) => setGObjetivo(e.target.value)} aria-label="Objetivo">
								<option value="">Objetivo…</option>
								{objetivosEtapa.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
							</select>
							{catalogs?.isSupervisor ? (
								<select value={gDuena} onChange={(e) => setGDuena(e.target.value)} aria-label="A cargo de">
									<option value="">A cargo de: yo</option>
									{(catalogs.advisors || []).map((a) => <option key={a.id} value={a.id}>{a.name}{a.team ? " — " + a.team : ""}</option>)}
								</select>
							) : null}
							<input type="text" placeholder="Para qué (opcional)" value={gMotivo} onChange={(e) => setGMotivo(e.target.value)} aria-label="Para qué" />
							<div className="fila-botones">
								<button className="btn-wa pri" disabled={!gObjetivo} onClick={abrirGestion}>Abrir gestión</button>
								<button className="btn-wa" onClick={() => setNuevaG(false)}>Cancelar</button>
							</div>
						</div>
					) : null}
				</div>
			) : null}

			{/* Vitrina (lienzo Matriculado): productos de la tienda para ofrecer por el chat. */}
			{act ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Vitrina</h4>{vitrina === null ? <button className="enlace" onClick={() => buscarVitrina("")}>Ver productos</button> : null}</div>
					{vitrina !== null ? (
						<>
							<input type="search" placeholder="Buscar producto" value={vBusca} aria-label="Buscar producto" onChange={(e) => setVBusca(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Enter") buscarVitrina(vBusca.trim()); }} />
							{vError ? <span className="oro">{vError}</span> : null}
							{vitrina.slice(0, 12).map((a) => (
								<div key={a.id} className="lado-oferta">
									<div><b>{a.name}</b><br /><span className="apagado">{dinero(Number(a.base_price), sim)}</span></div>
									<button className="btn-wa" onClick={() => onEnviarAlChat(`${a.name}: ${dinero(Number(a.base_price), sim)}. Se lo aparto?`)}>Ofrecer</button>
								</div>
							))}
							{!vitrina.length && !vError ? <span className="apagado">Nada con ese nombre.</span> : null}
						</>
					) : <span className="apagado">Artículos a la venta en el plantel.</span>}
				</div>
			) : null}

			{/* Qué ofrecerle hoy (Matriculado / Retirado) */}
			{w?.ofertas?.length ? (
				<div className="tarjeta">
					<h4>Qué ofrecerle hoy</h4>
					{w.ofertas.map((o, i) => (
						<div key={i} className="lado-oferta">
							<div><b>{o.title}</b><br /><span className="apagado">{o.detail}</span></div>
							{o.action ? <button className="btn-wa" onClick={() => accionOferta(o)}>{o.action}</button> : null}
						</div>
					))}
				</div>
			) : null}

			{/* Horarios abiertos: Pre-Inscripción y Retirado */}
			{!act ? (
				<div className="tarjeta" id="lado-horarios">
					<div className="tarjeta-cab"><h4>Horarios abiertos</h4><span className="cnt">{horarios ? abiertos.length : "…"}</span></div>
					{!cursoCotizar ? <span className="apagado">Sin curso de interés: ponlo en Editar.</span>
						: horarios && !abiertos.length ? <span className="apagado">No hay horarios por comenzar de ese curso en su plantel.</span> : null}
					{abiertos.map((h) => (
						<label key={h.id} className="cv-horario">
							<input type="checkbox" checked={marcados.includes(h.id)} onChange={(e) => setMarcados(e.target.checked ? [...marcados, h.id] : marcados.filter((x) => x !== h.id))} />
							<span><strong>{h.horario}</strong><br /><span className="apagado">{h.curso}{h.modulo && h.modulo !== h.curso ? " · " + h.modulo : ""} · inicia {h.inicioTexto}{h.maestro ? " · " + h.maestro : ""}</span></span>
						</label>
					))}
					{abiertos.length ? <button className="btn-wa oscuro" disabled={!marcados.length} onClick={enviarHorarios}>Enviar al chat</button> : null}
				</div>
			) : null}

			{/* Cotización: Pre-Inscripción y Retirado */}
			{!act && quote !== undefined ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Cotización</h4>{quote ? <span className="apagado">{cortoCurso(quote.curso)}</span> : null}</div>
					{!quote ? <span className="apagado">{cursoCotizar ? "No encontré ese curso en el catálogo del plantel." : "Pon el curso de interés en Editar."}</span> : (
						<>
							<div className="par"><span>Inscripción</span><span>{quote.inscripcion > 0 ? <><span className="tachado">{dinero(quote.inscripcion, quote.simbolo)}</span> gratis</> : "gratis"} <span className="apagado">esta semana</span></span></div>
							<div className="par"><span>Mensualidad × {quote.meses}</span><span>{dinero(quote.mensualidad, quote.simbolo)}</span></div>
							{quote.kit ? <div className="par"><span>{quote.kit.nombre}</span><span>{dinero(quote.kit.precio, quote.simbolo)}</span></div> : null}
							{quote.promo?.best ? <div className="par"><span>Promo</span><span>{quote.promo.best.name}</span></div> : null}
							<hr />
							<div className="par grande"><span>Hoy paga</span><span className="acento">{dinero(quote.hoy_paga, quote.simbolo)}</span></div>
							<span className="apagado">{quote.hoy_paga_detalle}</span>
							<button className="btn-wa oscuro" onClick={enviarCotizacion}>Enviar al chat</button>
						</>
					)}
				</div>
			) : null}

			<div className="tarjeta">
				<h4>Siguiente paso</h4>
				<div className="cv-pasos">
					<button className="btn-wa" onClick={() => onPaso("promesa")}>Promesa de visita</button>
					<button className="btn-wa" onClick={() => onPaso("llamar")}>Volver a llamar</button>
					<button className="btn-wa" onClick={() => onPaso("no_interesa")}>No le interesa</button>
				</div>
			</div>

			<div className="tarjeta">
				<h4>Vínculos</h4>
				{det.links.length ? det.links.map((l) => (
					<div key={l.id} className="par">
						<span>{l.kind.replace("_", " / ")}</span>
						<span><button className="enlace" onClick={() => onAbrir(l.prospect_id)}>{nombreCompleto(l)}</button>
							<button className="btn-wa mini" onClick={() => onQuitarVinculo(l.id)} aria-label="Quitar vínculo">×</button></span>
					</div>
				)) : <span className="apagado">Sin vínculos.</span>}
			</div>

			{det.zohoRecords.length ? (
				<details className="tarjeta">
					<summary><h4 style={{ display: "inline" }}>Venía de Zoho</h4></summary>
					{det.zohoRecords.map((r) => (
						<div key={r.zoho_id} className="par"><span>{r.zoho_module === "Leads" ? "Posible cliente" : "Contacto"}</span><span>{cuando(r.zoho_created_at)}</span></div>
					))}
				</details>
			) : null}
		</div>
	);
};

export default LadoCliente;
